import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'clsx';
import get from 'lodash/get';
import flow from 'lodash/flow';
import findIndex from 'lodash/findIndex';

// decorators
// components
// selectors
import { selectIsSubscriptionEnabled } from 'src/common/selectors/config';
import { selectIsUserSubscribed } from 'src/common/selectors/me';
import TileLinks from '../shared/tileLinks/TileLinks';
import ExternalLink from '../shared/link/ExternalLink';
import OutlinedPillButtonLink from '../shared/button/OutlinedPillButtonLink';
import CoralButtonLink from '../shared/button/CoralButtonLink';
import SearchBar from '../search/Searchbar';
import mobileDownload from '../../constants/analytics/categoryActionLabel/mobileDownload';
import SeoHelmet from '../SeoHelmet';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import connectWithExperiments from '../../decorators/connectWithExperiments';
import { selectValidatedHeroCarouselItems } from '../../selectors/heroCarousel';

// actions
import {
  logMobileDownloadClick,
  logSubscribeActivity,
  logWebActivity,
} from '../../actions/logging';

// utils
import { createLandingPageAndroidDeepLinks } from '../utils/createAndroidDeepLinks';
import { createLandingPageInternationalSeoLinks } from '../utils/createInternationalSeoLinks';
import { createLandingPageTitle } from '../utils/pageTitles';
import { createLandingPageMetaDescription } from '../utils/pageMetaDescriptions';
import { createLandingPageMetaKeywords } from '../utils/pageMetaKeywords';
import { googlePlayAppUrl, iTunesAppUrl } from '../../utils/downloadLinks';
import getSubscribeEventLabel from '../../utils/subscription/getSubscribeEventLabel';
import createSubscribeLink from '../../utils/subscription/createSubscribeLink';
import debounce from '../../utils/debounce';
import vars from '../../vars';

// constants
import { UPSELL_START_FREE_TRIAL_BTN } from '../../constants/localizations/upsell';
import {
  DISCOVER_UPPERCASE,
  EVERYWHERE_UPPERCASE,
  GO_UPPERCASE,
  LISTEN_UPPERCASE,
  MORE_UPPERCASE,
  MUSIC_UPPERCASE_PERIOD,
  NEWS_UPPERCASE_PERIOD,
  PODCASTS_UPPERCASE_PERIOD,
  PREMIUM_UPPERCASE,
  SPORTS_RADIO_UPPERCASE_PERIOD,
} from '../../constants/localizations/shared';
import {
  LANDING_DEVICES_PARAGRAPH,
  LANDING_DISCOVER_PARAGRAPH,
  LANDING_FINAL_CTA,
  LANDING_LEAD_PARAGRAPH,
  LANDING_LISTEN_NOW,
  LANDING_SUGGEST_SEARCH_V2,
  LANDING_VERTICALS_MUSIC_DESCRIPTION,
  LANDING_VERTICALS_NEWS_DESCRIPTION,
  LANDING_VERTICALS_PODCASTS_DESCRIPTION,
  LANDING_VERTICALS_SPORTS_DESCRIPTION,
} from '../../constants/localizations/landing';
import {
  HOME_BROWSE_PATH,
  MUSIC_BROWSE_PATH,
  NEWS_BROWSE_PATH,
  PODCASTS_BROWSE_PATH,
  SPORTS_BROWSE_PATH,
} from '../../constants/paths';
import cssVariables from '../../styles/variables';
import {
  MONTHLY_SUBSCRIPTION,
  YEARLY_SUBSCRIPTION,
} from '../../constants/experiments/skus';
import {
  NEW_LANDING_UPSELL_ENABLED,
  NEW_LANDING_UPSELL_PRODUCT,
  UPSELL_CTA_TEXT,
} from '../../constants/experiments/upsell';
import subscribe, {
  labels,
} from '../../constants/analytics/categoryActionLabel/subscribe';
import {
  utmCampaignLandingPage,
  utmMedium,
  utmSource,
} from '../../constants/analytics/utm';
import {
  Divider,
  HeroUpsell,
  LandingPageText,
  LandingStartListeningButton,
  LandingUpsell,
  NewLandingUpsell,
  PremiumOfferingTable,
  SectionHeader,
  SectionText,
} from './LandingComponents';
import HeroCarousel from './carousel/HeroCarousel';
import WideHeader from './WideHeader';
import ParallaxHeaders from './ParallaxHeaders';
import css from './landing.module.scss';

const utmParams = {
  utm_medium: utmMedium,
  utm_source: utmSource,
  utm_campaign: utmCampaignLandingPage,
};

/* eslint-disable max-len */
const parallaxScrollObjects = [
  {
    dataTestId: 'news',
    header: NEWS_UPPERCASE_PERIOD,
    subtitle: LANDING_VERTICALS_NEWS_DESCRIPTION,
    href: NEWS_BROWSE_PATH,
    imageBlock: [
      {
        guideItem: { actions: { play: { guideId: 's20407' } } },
        imageUrl: 'assets/img/landing/parallaxImages/cnn-large-v2.jpg',
        href: '/cnn/',
      },
      {
        guideItem: { actions: { play: { guideId: 's20431' } } },
        imageUrl: 'assets/img/shared/tiles/fox-news-radio-small.jpg',
        href: '/radio/FOX-News-Talk-s20431/',
        needsBorder: true,
      },
      {
        guideItem: { actions: { play: { guideId: 's110052' } } },
        imageUrl: 'assets/img/shared/tiles/cnbc-small.jpg',
        href: '/radio/CNBC-s110052/',
        needsBorder: true,
      },
      {
        guideItem: { actions: { play: { guideId: 's297990' } } },
        imageUrl: 'assets/img/shared/tiles/msnbc-square.jpg',
        href: '/radio/MSNBC-s297990/',
      },
    ],
    isFirstImageSmall: false,
  },
  {
    dataTestId: 'sports',
    header: SPORTS_RADIO_UPPERCASE_PERIOD,
    subtitle: LANDING_VERTICALS_SPORTS_DESCRIPTION,
    href: SPORTS_BROWSE_PATH,
    imageBlock: [
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/nfl-large-v1.jpg',
        href: '/radio/NFL-Football-Radio-c1736013/',
      },
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/nhl-small-v2.jpg',
        href: '/radio/NHL-Radio--Stream-Hockey-Radio-c393481/',
      },
      {
        guideItem: { actions: { play: { guideId: 's22469' } } },
        imageUrl: 'assets/img/shared/tiles/espn-small.jpg',
        href: '/radio/987-FM-ESPN-New-York-s22469/',
      },
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/learfield-square.jpg',
        href: '/radio/Live-Stream-College-Sports-Radio-c100003401/',
      },
    ],
    isFirstImageSmall: false,
  },
  {
    dataTestId: 'music',
    header: MUSIC_UPPERCASE_PERIOD,
    subtitle: LANDING_VERTICALS_MUSIC_DESCRIPTION,
    href: MUSIC_BROWSE_PATH,
    imageBlock: [
      {
        guideItem: { actions: { play: { guideId: 's249995' } } },
        imageUrl: 'assets/img/shared/tiles/latin-hits-small.jpg',
        href: '/radio/Latin-Hits-s249995/',
      },
      {
        guideItem: { actions: { play: { guideId: 's224628' } } },
        imageUrl: 'assets/img/landing/parallaxImages/country-roads-v2.jpg',
        href: '/countryroads/',
      },
      {
        guideItem: { actions: { play: { guideId: 's242677' } } },
        imageUrl: 'assets/img/shared/tiles/todays-hits-large.jpg',
        href: '/todays-hits/',
      },
      {
        guideItem: { actions: { play: { guideId: 's249994' } } },
        imageUrl: 'assets/img/shared/tiles/classic-rock-hits-small.jpg',
        href: '/radio/Classic-Rock-Hits-s249994/',
      },
    ],
    isFirstImageSmall: true,
  },
  {
    dataTestId: 'podcasts',
    header: PODCASTS_UPPERCASE_PERIOD,
    subtitle: LANDING_VERTICALS_PODCASTS_DESCRIPTION,
    href: PODCASTS_BROWSE_PATH,
    imageBlock: [
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/this-am-life-small.jpg',
        href: '/podcasts/Storytelling/This-American-Life-p28/',
      },
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/wow-medium.jpg',
        href: '/podcasts/Kids--Family-Podcasts/Wow-in-the-World-p1086001/',
      },
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/the-daily-large.jpg',
        href: '/podcasts/News--Politics-Podcasts/The-Daily-p952868/',
      },
      {
        guideItem: { actions: { play: { guideId: '' } } },
        imageUrl: 'assets/img/shared/tiles/npr-hidden-brain-small.jpg',
        href: '/podcasts/Science-Podcasts/Hidden-Brain-p787503/',
      },
    ],
    isFirstImageSmall: true,
  },
];
/* eslint-enable max-len */

const lightBlueHeaderColor = '#7FCFEC';

const handleAppDownloadClick = debounce((logAction, label) => logAction(label));

class Landing extends Component {
  static propTypes = {
    breakpoint: PropTypes.number.isRequired,
    actions: PropTypes.shape({
      logMobileDownloadClick: PropTypes.func.isRequired,
      logSubscribeActivity: PropTypes.func.isRequired,
      logWebActivity: PropTypes.func.isRequired,
    }),
    products: PropTypes.object.isRequired,
    carouselElementList: PropTypes.array.isRequired,
    isSubscriptionEnabled: PropTypes.bool.isRequired,
    isUserSubscribed: PropTypes.bool.isRequired,
    shouldShowPremiumUpsell: PropTypes.bool.isRequired,
    [NEW_LANDING_UPSELL_ENABLED]: PropTypes.bool.isRequired,
    [NEW_LANDING_UPSELL_PRODUCT]: PropTypes.string,
    [UPSELL_CTA_TEXT]: PropTypes.string,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.handleCarouselChange = this.handleCarouselChange.bind(this);

    this.state = {
      carouselSelectedIndex: 0,
    };
  }

  componentDidMount() {
    const { actions, shouldShowPremiumUpsell } = this.props;

    if (shouldShowPremiumUpsell) {
      const { location } = this.context;
      let eventLabel = getSubscribeEventLabel(
        labels.landingTopUpsell,
        location,
      );
      actions.logSubscribeActivity(subscribe.actions.show, eventLabel);
      eventLabel = getSubscribeEventLabel(
        labels.landingGoPremiumUpsell,
        location,
      );
      actions.logSubscribeActivity(subscribe.actions.show, eventLabel);
      eventLabel = getSubscribeEventLabel(
        labels.landingFinalCtaUpsell,
        location,
      );
      actions.logSubscribeActivity(subscribe.actions.show, eventLabel);
      // Log carousel upsell show event for when the first slide is the upsell
      this.logCarouselShowEvent();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.shouldShowPremiumUpsell !== prevProps.shouldShowPremiumUpsell
    ) {
      // allowed within a condition, the warning is to help users be careful with the usage
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        carouselSelectedIndex: 0,
      });
    }
  }

  handleCarouselChange(currentIndex) {
    this.setState({
      carouselSelectedIndex: currentIndex,
    });
    this.logCarouselShowEvent();
  }

  logCarouselShowEvent() {
    const { actions, shouldShowPremiumUpsell } = this.props;

    // Log carousel upsell show event every time the upsell is in view
    if (shouldShowPremiumUpsell) {
      const carouselUpsellIndex = findIndex(
        this.props.carouselElementList,
        (o) => o.isCarouselUpsell,
      );
      if (carouselUpsellIndex === this.state.carouselSelectedIndex) {
        const eventLabel = getSubscribeEventLabel(
          labels.landingCarouselUpsell,
          location,
        );
        actions.logSubscribeActivity(subscribe.actions.show, eventLabel);
      }
    }
  }

  render() {
    const {
      actions,
      breakpoint,
      carouselElementList,
      products,
      shouldShowPremiumUpsell,
      [NEW_LANDING_UPSELL_ENABLED]: newLandingUpsellEnabled,
      [NEW_LANDING_UPSELL_PRODUCT]: newLandingUpsellProduct,
      [UPSELL_CTA_TEXT]: upsellCtaText,
      [YEARLY_SUBSCRIPTION]: yearlyProductSku,
      [MONTHLY_SUBSCRIPTION]: monthlyProductSku,
    } = this.props;

    const { getLocalizedText, location } = this.context;
    const { carouselSelectedIndex } = this.state;

    const yearlySku = yearlyProductSku || vars.get('YEARLY_SKU');
    const monthlySku = monthlyProductSku || vars.get('MONTHLY_SKU');
    const currencyCode = products?.[monthlySku]?.currencyCode || 'USD';

    const monthlyAndYearlyProducts = {
      monthly: products?.[monthlySku],
      yearly: products?.[yearlySku],
      currencyCode,
    };

    const links = [
      ...createLandingPageInternationalSeoLinks(),
      ...createLandingPageAndroidDeepLinks(),
    ];

    const slideBackgroundColor = newLandingUpsellEnabled
      ? cssVariables['--secondary-color-5']
      : get(
          carouselElementList[carouselSelectedIndex],
          'bgColor',
          cssVariables['--black'],
        );
    const areColorsInverted = newLandingUpsellEnabled
      ? true
      : slideBackgroundColor === cssVariables['--black'];

    return (
      <div className={css.landingPage}>
        <SeoHelmet
          title={createLandingPageTitle(getLocalizedText)}
          description={createLandingPageMetaDescription(getLocalizedText)}
          keywords={createLandingPageMetaKeywords()}
          links={links}
          includeSEOScript
        />
        <div
          className={classNames(css.topSection, {
            [css.newLandingUpsellBackground]: newLandingUpsellEnabled,
          })}
          style={{ backgroundColor: slideBackgroundColor }}
        >
          <WideHeader
            logSubscribeActivity={this.props.actions.logSubscribeActivity}
            isUserSubscribed={this.props.isUserSubscribed}
            subscriptionEnabled={this.props.isSubscriptionEnabled}
            breakpoint={breakpoint}
            areColorsInverted={areColorsInverted}
          />
          {!newLandingUpsellEnabled && (
            <HeroCarousel
              carouselElements={carouselElementList}
              selectedIndex={carouselSelectedIndex}
              handleCarouselChange={this.handleCarouselChange}
              logSubscribeActivity={this.props.actions.logSubscribeActivity}
              logWebActivity={this.props.actions.logWebActivity}
              areColorsInverted={areColorsInverted}
            />
          )}
          {newLandingUpsellEnabled && (
            <HeroUpsell
              linkTo={createSubscribeLink({
                source: subscribe.labels.landingTopUpsell,
                additionalParams: {
                  sku: newLandingUpsellProduct,
                },
                location,
              })}
              onClick={() =>
                actions.logSubscribeActivity(
                  subscribe.actions.tap,
                  subscribe.labels.landingTopUpsell,
                )
              }
            />
          )}
        </div>
        <div className={css.searchBarContainer}>
          <SearchBar
            iconContainerClassName={css.searchIconContainer}
            placeholder={getLocalizedText(LANDING_SUGGEST_SEARCH_V2)}
            iconProps={{
              width: '26px',
              height: '26px',
              fill: cssVariables['--secondary-color-5'],
            }}
          />
        </div>

        {!newLandingUpsellEnabled && (
          <div>
            <LandingPageText
              data-testid="homeLeadParagraph"
              text={getLocalizedText(LANDING_LEAD_PARAGRAPH)}
            />
            <LandingStartListeningButton
              id="homeStartListeningButton1"
              data-testid="startListeningButton1"
            />

            {shouldShowPremiumUpsell && (
              <LandingUpsell
                linkTo={createSubscribeLink({
                  source: subscribe.labels.landingTopUpsell,
                  location,
                })}
                onClick={() =>
                  actions.logSubscribeActivity(
                    subscribe.actions.tap,
                    subscribe.labels.landingTopUpsell,
                  )
                }
              />
            )}
          </div>
        )}
        {newLandingUpsellEnabled && shouldShowPremiumUpsell && (
          <NewLandingUpsell
            linkTo={createSubscribeLink({
              source: subscribe.labels.landingTopUpsell,
              additionalParams: {
                sku: newLandingUpsellProduct,
              },
              location,
            })}
            onClick={() =>
              actions.logSubscribeActivity(
                subscribe.actions.tap,
                subscribe.labels.landingTopUpsell,
              )
            }
          />
        )}
        <ParallaxHeaders
          breakpoint={breakpoint}
          objects={parallaxScrollObjects}
        />

        {shouldShowPremiumUpsell && (
          <div className={css.premiumOfferingsBackground}>
            <div className={css.premiumOfferingsContainer}>
              <SectionHeader
                data-testid="premiumHeaderText"
                headerLine1={getLocalizedText(GO_UPPERCASE)}
                headerLine2={getLocalizedText(PREMIUM_UPPERCASE)}
                headerLine1Color="white"
                headerLine2Color={cssVariables['--tunein-coral']}
              />

              <div className={css.premiumOfferings}>
                <PremiumOfferingTable
                  data-testid="premiumOfferingTable"
                  isFreeOffering
                  products={monthlyAndYearlyProducts}
                >
                  <OutlinedPillButtonLink
                    to={HOME_BROWSE_PATH}
                    id="homeStartListeningButton2"
                    data-testid="homeStartListeningButton2"
                    className={classNames(
                      css.button,
                      css.startListeningButton2,
                    )}
                    label={getLocalizedText(LANDING_LISTEN_NOW)}
                  />
                </PremiumOfferingTable>

                <PremiumOfferingTable
                  data-testid="premiumOfferingTable"
                  products={monthlyAndYearlyProducts}
                >
                  <CoralButtonLink
                    id="landingGoPremiumUpsell"
                    dataTestId="landingGoPremiumUpsell"
                    to={createSubscribeLink({
                      source: subscribe.labels.landingGoPremiumUpsell,
                      location,
                    })}
                    className={classNames(css.button, css.startFreeTrialButton)}
                    label={getLocalizedText(
                      upsellCtaText || UPSELL_START_FREE_TRIAL_BTN,
                    )}
                    onClick={() =>
                      actions.logSubscribeActivity(
                        subscribe.actions.tap,
                        subscribe.labels.landingGoPremiumUpsell,
                      )
                    }
                  />
                </PremiumOfferingTable>
              </div>
            </div>
          </div>
        )}
        <div className={css.deviceOfferingsContainer}>
          <div data-testid="CEImages" className={css.deviceOfferingsImages} />

          <div className={css.deviceTextAndLinks}>
            <SectionHeader
              data-testid="CEHeaderText"
              headerLine1={getLocalizedText(LISTEN_UPPERCASE)}
              headerLine2={getLocalizedText(EVERYWHERE_UPPERCASE)}
              headerLine2Color={lightBlueHeaderColor}
            />
            <SectionText
              data-testid="CEBodyText"
              className={css.devicesText}
              text={getLocalizedText(LANDING_DEVICES_PARAGRAPH)}
            />

            <div
              className={css.deviceLinksContainer}
              data-testid="landingDeviceLinksContainer"
            >
              <div className={css.appDownloadLink}>
                <ExternalLink
                  data-testid="homeDownloadApple"
                  href={iTunesAppUrl(utmParams)}
                >
                  <span
                    onClick={() => {
                      handleAppDownloadClick(
                        actions.logMobileDownloadClick,
                        mobileDownload.labels.ios,
                      );
                    }}
                  >
                    <div className={css.appleIcon} />
                  </span>
                </ExternalLink>
              </div>
              <div className={css.appDownloadLink}>
                <ExternalLink
                  data-testid="homeDownloadAndroid"
                  href={googlePlayAppUrl(utmParams)}
                >
                  <span
                    onClick={() => {
                      handleAppDownloadClick(
                        actions.logMobileDownloadClick,
                        mobileDownload.labels.android,
                      );
                    }}
                  >
                    <div className={css.googlePlayIcon} />
                  </span>
                </ExternalLink>
              </div>
            </div>
          </div>
        </div>

        <Divider />

        <div className={css.discoverContainer}>
          <SectionHeader
            data-testid="homeDiscoverHeaderText"
            headerLine1={getLocalizedText(DISCOVER_UPPERCASE)}
            headerLine2={getLocalizedText(MORE_UPPERCASE)}
            headerLine2Color={cssVariables['--t-sharp']}
          />
          <SectionText
            data-testid="homeDiscoverBodyText"
            className={css.sectionTextDiscover}
            text={getLocalizedText(LANDING_DISCOVER_PARAGRAPH)}
          />
        </div>

        <TileLinks />

        <div className={css.finalCta}>
          <p>{getLocalizedText(LANDING_FINAL_CTA)}</p>
          <div className={css.finalCtaLinksContainer}>
            <LandingStartListeningButton
              id="homeStartListeningButton3"
              data-testid="startListeningButton3"
              className={css.finalCtaListenNowButton}
            />
            {shouldShowPremiumUpsell && (
              <CoralButtonLink
                id="finalCtaStartFreeTrialButton"
                dataTestId="finalCtaStartFreeTrialButton"
                to={createSubscribeLink({
                  source: subscribe.labels.landingFinalCtaUpsell,
                  location,
                })}
                className={css.finalCtaStartFreeTrialButton}
                label={getLocalizedText(
                  upsellCtaText || UPSELL_START_FREE_TRIAL_BTN,
                )}
                onClick={() =>
                  actions.logSubscribeActivity(
                    subscribe.actions.tap,
                    subscribe.labels.landingFinalCtaUpsell,
                  )
                }
              />
            )}
          </div>
        </div>

        <div className={css.finalCtaDivider} data-testid="finalCtaDivider">
          <div data-testid="finalCtaDividerDiv" />
        </div>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  const actions = {
    logMobileDownloadClick,
    logSubscribeActivity,
    logWebActivity,
  };
  return { actions: bindActionCreators(actions, dispatch) };
}

export function mapStateToProps(state) {
  const products = get(state, 'products');
  const isUserSubscribed = selectIsUserSubscribed(state);
  const isSubscriptionEnabled = selectIsSubscriptionEnabled(state);
  const carouselElementList = selectValidatedHeroCarouselItems(state);

  return {
    carouselElementList,
    isUserSubscribed,
    isSubscriptionEnabled,
    shouldShowPremiumUpsell: isSubscriptionEnabled && !isUserSubscribed,
    products,
  };
}

export default flow(
  connectWithExperiments([
    MONTHLY_SUBSCRIPTION,
    YEARLY_SUBSCRIPTION,
    UPSELL_CTA_TEXT,
    NEW_LANDING_UPSELL_ENABLED,
    NEW_LANDING_UPSELL_PRODUCT,
  ]),
  connect(mapStateToProps, mapDispatchToProps),
)(Landing);
